import { useEffect, useMemo, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useTabsOff, registerHiddenTabs, useOpenTabListener } from "@/lib/tabToggle";
import {
  BookmarkSimple, CaretRight, CaretDown, Plus, X, Wrench, Trash, PencilSimple,
  Lock, FloppyDisk, ArrowSquareOut, Sparkle, Export,
} from "@phosphor-icons/react";
import * as Phosphor from "@phosphor-icons/react";
import { toast } from "sonner";
import { Link, useNavigate } from "react-router-dom";
import { api, getToken, getUser } from "@/lib/api";
import { useVoiceQA } from "@/lib/useVoiceQA";
import SiloPickerModal from "@/components/SiloPickerModal";
import UnlockInteractions from "@/components/UnlockInteractions";
import { useBumpConfig, slotToTopPct, COLOR_SLOT_VAR, SHAPE_CLASS, SHAPE_CLASS_RIGHT } from "@/lib/bumpConfig";

const GHOST_KEY_PREFIX = "ghost_bookmarks__"; // localStorage key per silo slug
const GHOST_CAP = 3;

const firstNWords = (s, n) => (s || "").trim().split(/\s+/).slice(0, n).join(" ");

// =====================================================================
// Public surface: <SideBumps> renders TWO independent left-edge tabs:
//   - LowerBump (under the eye-line) → action buttons (Bookmark current chat,
//     Send to staging, Commit to silo) — kept compact for thumb-reach on mobile.
//   - UpperBump (taller, above eye-line) → Bookmark Shelf grouped by named
//     sections, designed to be the persistent revenue funnel for anonymous →
//     subscriber → creator conversion.
//
// `mode = "left"` for now; right-side bumps (Discovery / Invite) reuse the same
// component with `mode="right"` in a later iteration.
// =====================================================================
export default function SideBumps({ silo, messages, setMessages, onLoadBookmark, mode = "left", voiceUserSelectable = false, voiceOptions = [], selectedVoice, onSelectVoice, interactions = null, onInteractionTip, onInteractionSubscribe, onInteractionFavorite, onInteractionAddHomeScreen, onBookmarkSaved = null }) {
  const user = getUser();
  const isAuthed = !!getToken();

  // iter-35g: pull bump positions/dims from the platform-global Bump Studio
  // config. We render the legacy Bookmarks + Tools surfaces, but their
  // size/shape/slot/color/icon/name come from the admin config.
  const { cfg: bumpCfg, viewport: bumpVp } = useBumpConfig();
  const sideKey = mode === "right" ? "right" : "left";
  const sideCfg = bumpCfg ? (bumpCfg[sideKey]?.[bumpVp] || { bumps: [], max_slots: 8 }) : null;
  const cfgById = useMemo(() => {
    const map = {};
    (sideCfg?.bumps || []).forEach((b) => { map[b.id] = b; });
    return map;
  }, [sideCfg]);
  const bookmarksCfg = cfgById["bookmarks"];
  const toolsCfg = cfgById["tools"];

  const [openShelf, setOpenShelf] = useState(false);
  const [openUtility, setOpenUtility] = useState(false);
  // Jul 2026 (Randy) — "Turn off tabs": hide the edge handles and list these
  // surfaces in the hamburger selector instead (SiloHeaderNav).
  const tabsOff = useTabsOff();
  useEffect(() => {
    const tabs = [];
    if (bookmarksCfg) tabs.push({ key: `${mode}-bookmarks`, label: bookmarksCfg.name || "Bookmarks" });
    if (toolsCfg) tabs.push({ key: `${mode}-tools`, label: toolsCfg.name || "Tools" });
    return registerHiddenTabs(`side-bumps-${mode}`, tabs);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookmarksCfg?.name, toolsCfg?.name, mode]);
  useOpenTabListener((key) => {
    if (key === `${mode}-bookmarks`) setOpenShelf(true);
    if (key === `${mode}-tools`) setOpenUtility(true);
  });
  // Jun 2026 — visitor billing: the Q4 nudge + the closing of the Favorite CTA
  // auto-open this Tools drawer so the "Unlock interactions" board is revealed.
  useEffect(() => {
    if (mode !== "left") return;
    const onOpen = () => setOpenUtility(true);
    window.addEventListener("mysilo:open-tools-drawer", onOpen);
    return () => window.removeEventListener("mysilo:open-tools-drawer", onOpen);
  }, [mode]);

  const [bookmarks, setBookmarks] = useState([]);
  const [ghosts, setGhosts] = useState([]);
  const [loadingBookmarks, setLoadingBookmarks] = useState(false);
  // Jun 2026 (Randy) — play a bookmark's COMPLETE 7-day audio straight from the
  // shelf (free) through the app's PERSISTENT two-voice engine, so it plays in two
  // voices AND keeps playing when you close this drawer or move around the app.
  const [playingBmId, setPlayingBmId] = useState(null);
  const bmVoice = useVoiceQA({ slug: silo?.slug });
  const stopBmAudio = useCallback(() => {
    try { bmVoice.stopSpeaking(); } catch { /* */ } setPlayingBmId(null);
  }, [bmVoice]);
  const playBmAudio = useCallback(async (b) => {
    if (playingBmId === b.id) { stopBmAudio(); return; }
    stopBmAudio();
    try {
      const r = await api.get(`/bookmarks/${b.id}/audio`);
      const chunks = (r.data?.chunks || []).map((c) => c.audio_base64).filter(Boolean);
      if (!chunks.length) { toast("That saved audio has expired — open the conversation to rebuild it.", { icon: "↪" }); return; }
      setPlayingBmId(b.id);
      bmVoice.playPrefetched(chunks, { key: `bm-${b.id}` })
        .finally(() => setPlayingBmId((p) => (p === b.id ? null : p)));
    } catch { toast.error("Could not play the saved audio."); }
  }, [playingBmId, stopBmAudio, bmVoice]);

  const [bookmarkModal, setBookmarkModal] = useState({ open: false });
  const [signupNudge, setSignupNudge] = useState({ open: false, kind: "anon" }); // anon | subscriber-cap
  const [siloPicker, setSiloPicker] = useState({ open: false, mode: "commit" }); // commit | staging
  // Sprint Feb 7, 2026 — bookmark tap-to-load. When the user taps a bookmark
  // and the chat already has unsaved messages, surface this confirmation
  // before clobbering them. Three actions: save the current chat as a new
  // bookmark first, discard, or cancel.
  const [loadConfirm, setLoadConfirm] = useState({ open: false, target: null });

  // Replay a bookmark's stored messages into the live chat feed. Pure
  // frontend — bookmarks already store full role/text turns server-side.
  // No auto-summarize / context-bridge runs at save or load time, so we
  // get the conversation start-to-finish.
  const replayBookmark = useCallback((b) => {
    if (!setMessages) return;
    // Jun 2026 (Randy) — prefer the page's loader: it stamps ?bookmark=<id> into
    // the URL so refresh AND share keep the EXACT loaded thread (bug fix).
    if (onLoadBookmark) {
      onLoadBookmark(b);
      setOpenShelf(false);
      toast.success(`Loaded "${b.file_title}".`);
      return;
    }
    const turns = (b.messages || []).map((m) => ({
      role: m.role,
      text: m.text,
      // Bookmarks predate per-turn metadata flags; render as standard turns.
      outOfScope: !!m.outOfScope,
    }));
    setMessages(turns);
    setOpenShelf(false);
    toast.success(`Loaded "${b.file_title}".`);
  }, [setMessages, onLoadBookmark]);

  // Tap handler: if there's an unsaved live conversation, gate behind the
  // load-confirm modal. Otherwise replay immediately.
  const onTapBookmark = useCallback((b) => {
    if (b.ghost) {
      // Ghost rows can't be loaded — they're locked behind signup.
      toast("Sign up free to unlock saved conversations.", { icon: "🔒" });
      return;
    }
    const hasLiveChat = (messages || []).length > 0;
    if (hasLiveChat) {
      setLoadConfirm({ open: true, target: b });
      return;
    }
    replayBookmark(b);
  }, [messages, replayBookmark]);

  const refresh = useCallback(async () => {
    if (!isAuthed) {
      try {
        const raw = localStorage.getItem(GHOST_KEY_PREFIX + silo.slug);
        setGhosts(raw ? JSON.parse(raw) : []);
      } catch { setGhosts([]); }
      return;
    }
    setLoadingBookmarks(true);
    try {
      const r = await api.get(`/bookmarks?slug=${encodeURIComponent(silo.slug)}`);
      setBookmarks(r.data || []);
    } catch { /* silent */ } finally { setLoadingBookmarks(false); }
  }, [isAuthed, silo.slug]);

  useEffect(() => { refresh(); }, [refresh]);

  // Whenever the bookmark shelf opens, refresh so creators see new bookmarks made
  // from another tab/device. Cheap call.
  useEffect(() => { if (openShelf) refresh(); }, [openShelf, refresh]);

  // Group bookmarks by section_name. Ghosts get bucketed under "Saved (sign up to access)".
  const sections = useMemo(() => {
    const map = new Map();
    const list = isAuthed ? bookmarks : ghosts;
    for (const b of list) {
      const key = (isAuthed ? b.section_name : (b.section_name || "Saved (sign up to access)")) || "Untitled section";
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(b);
    }
    return Array.from(map.entries()).map(([name, items]) => ({ name, items }));
  }, [isAuthed, bookmarks, ghosts]);

  const totalCount = (isAuthed ? bookmarks : ghosts).length;

  const onClickBookmark = () => {
    if (!messages?.length) {
      toast("Ask something first — there's nothing to bookmark.", { icon: "↪" });
      return;
    }
    if (!isAuthed) {
      // Anonymous → kickstart signup AND offer ghost continuation
      setSignupNudge({ open: true, kind: "anon" });
      return;
    }
    setBookmarkModal({ open: true });
  };

  const continueAsGhost = () => {
    setSignupNudge({ open: false, kind: "anon" });
    if (ghosts.length >= GHOST_CAP) {
      toast.error(`Anonymous bookmark cap reached (${GHOST_CAP}). Sign up to keep going.`);
      return;
    }
    setBookmarkModal({ open: true, ghost: true });
  };

  // Sprint Feb 8, 2026 — duplicate bookmark name handling. If user tries to
  // save a bookmark with the same (section_name, file_title) as an existing
  // one, prompt for overwrite. Overwrite = delete old + insert new (the
  // newer growth replaces the older one). This avoids two bookmarks under
  // identical labels in the shelf — which made it ambiguous which one a
  // tap would load.
  const [overwriteConfirm, setOverwriteConfirm] = useState({ open: false, existing: null, payload: null, ghost: false });

  const saveBookmark = async ({ section_name, file_title, ghost, _confirmedOverwrite = false }) => {
    const payload = {
      slug: silo.slug,
      section_name,
      file_title,
      origin: "public",
      messages: messages.map((m) => ({ role: m.role === "ai" ? "ai" : m.role, text: m.text })),
    };

    // Duplicate-name guard — runs for both ghost and authed flows. We compare
    // against whichever store is active for this user (ghosts vs DB rows).
    if (!_confirmedOverwrite) {
      const haystack = ghost || !isAuthed ? ghosts : bookmarks;
      const sectionMatch = (b) => (b.section_name || "") === section_name;
      const titleMatch = (b) => (b.file_title || "") === file_title;
      const existing = haystack.find((b) => sectionMatch(b) && titleMatch(b));
      if (existing) {
        // Pause and ask the user before destroying the older row.
        setBookmarkModal({ open: false });
        setOverwriteConfirm({ open: true, existing, payload: { section_name, file_title }, ghost: !!ghost });
        return;
      }
    }
    if (ghost || !isAuthed) {
      try {
        const next = [...ghosts, {
          id: `ghost_${Date.now()}`,
          ghost: true,
          ...payload,
          created_at: new Date().toISOString(),
        }].slice(-GHOST_CAP);
        localStorage.setItem(GHOST_KEY_PREFIX + silo.slug, JSON.stringify(next));
        setGhosts(next);
        // Jun 2026 (Randy) — "bookmarking it as a ghost should have given me a
        // dot. But it did not." The award endpoint existed; nothing called it.
        onBookmarkSaved?.();
        toast.success("Saved as ghost bookmark — sign up to make it clickable.");
      } catch {
        toast.error("Could not save locally.");
      }
      setBookmarkModal({ open: false });
      return;
    }
    try {
      const r = await api.post("/bookmarks", payload);
      setBookmarks((prev) => [r.data, ...prev]);
      onBookmarkSaved?.();
      toast.success("Bookmarked.");
      setBookmarkModal({ open: false });
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (e.response?.status === 409 && detail?.code === "SUBSCRIBER_CAP_REACHED") {
        // Subscriber tried 2nd bookmark — show upgrade prompt + offer ghost continuation
        setSignupNudge({ open: true, kind: "subscriber-cap" });
        setBookmarkModal({ open: false });
        return;
      }
      toast.error(typeof detail === "string" ? detail : (detail?.message || "Could not save bookmark."));
    }
  };

  const removeBookmark = async (b) => {
    if (b.ghost) {
      const next = ghosts.filter((g) => g.id !== b.id);
      localStorage.setItem(GHOST_KEY_PREFIX + silo.slug, JSON.stringify(next));
      setGhosts(next);
      toast.success("Removed.");
      return;
    }
    try {
      await api.delete(`/bookmarks/${b.id}`);
      setBookmarks((prev) => prev.filter((x) => x.id !== b.id));
      toast.success("Removed.");
    } catch { toast.error("Could not remove."); }
  };

  const renameBookmark = async (b, patch) => {
    if (b.ghost) {
      const next = ghosts.map((g) => g.id === b.id ? { ...g, ...patch } : g);
      localStorage.setItem(GHOST_KEY_PREFIX + silo.slug, JSON.stringify(next));
      setGhosts(next);
      return;
    }
    try {
      const r = await api.patch(`/bookmarks/${b.id}`, patch);
      setBookmarks((prev) => prev.map((x) => x.id === b.id ? r.data : x));
    } catch { toast.error("Could not rename."); }
  };

  // Feb 14, 2026 PM session #5 — Push bookmark to a silo's workbench
  // staging area. Replaces the old Rename pencil per user request
  // ("editing the name of a note once set, is pointless"). Opens a
  // small modal that lets the user pick which of their silos receives
  // the dump. The conversation gets serialized to markdown and POSTed
  // to that silo's workbench-state.staging field.
  const [pushStagingState, setPushStagingState] = useState({ open: false, bookmark: null });
  const openPushToStaging = (bookmark) => {
    if (bookmark.ghost) {
      toast.error("Sign up first to push bookmarks to a workbench.");
      return;
    }
    setPushStagingState({ open: true, bookmark });
  };
  const closePushToStaging = () => setPushStagingState({ open: false, bookmark: null });

  const sideClass = mode === "right" ? "right-0" : "left-0";

  // iter-24 (Side-Bump P2): creator-only commit/staging actions on the public chat.
  // Subscribers/anon get the SignupNudge so the UX is consistent with bookmark capping.
  // Feb 21, 2026 — gate on paid platform subscription, not just role.
  // Every signup defaults to role="creator" before they've actually paid
  // the paid-creator gate, so role alone lets fans through.
  const isCreator = !!user && (
    user.role === "super_admin" ||
    user.platform_subscription_status === "active" ||
    user.is_affiliate === true
  );
  const openCommit = () => {
    if (!messages?.length) return toast("Ask something first.", { icon: "↪" });
    if (!isAuthed) { setSignupNudge({ open: true, kind: "anon" }); return; }
    if (!isCreator) { setSignupNudge({ open: true, kind: "subscriber-cap" }); return; }
    setSiloPicker({ open: true, mode: "commit" });
  };
  const openStaging = () => {
    if (!messages?.length) return toast("Ask something first.", { icon: "↪" });
    if (!isAuthed) { setSignupNudge({ open: true, kind: "anon" }); return; }
    if (!isCreator) { setSignupNudge({ open: true, kind: "subscriber-cap" }); return; }
    setSiloPicker({ open: true, mode: "staging" });
  };

  return (
    <>
      {/* === Lower bump — the action-button drawer (id=tools) === */}
      {toolsCfg && !tabsOff && (
      <BumpTab
        side={mode}
        open={openUtility}
        onOpen={() => setOpenUtility((v) => !v)}
        label="Tools"
        icon={Wrench}
        testid={`${mode}-bump-utility-tab`}
        cfg={toolsCfg}
        maxSlots={sideCfg?.max_slots}
      />)}
      {toolsCfg && (
      <Drawer open={openUtility} side={mode} onClose={() => setOpenUtility(false)} testid={`${mode}-bump-utility-drawer`} title={toolsCfg.name || "Tools"} cfg={toolsCfg}>
        <div className="flex flex-col h-full">
          <div className="space-y-2">
            <ActionButton
              icon={BookmarkSimple}
              label="Bookmark this conversation"
              sub={messages?.length ? `${messages.length} message${messages.length === 1 ? "" : "s"} in current view` : "Ask first"}
              disabled={!messages?.length}
              onClick={onClickBookmark}
              testid="bump-action-bookmark"
            />
            {/* Feb 21, 2026 — These two actions are creator-only and were
                previously rendered (with a "Creator only" sub-label) for
                everyone. That confused fans who tapped them and got bounced
                to a signup nudge. Hide them entirely unless the viewer is
                actually a paid creator. Bookmarks stay visible for all
                signed-in users (capped at 1 per silo for fans). */}
            {isCreator && (
              <>
                <ActionButton
                  icon={Plus}
                  label="Send to staging area"
                  sub="Pick a silo's Workbench staging"
                  disabled={!messages?.length}
                  onClick={openStaging}
                  testid="bump-action-send-staging"
                />
                <ActionButton
                  icon={Sparkle}
                  label="Commit to silo"
                  sub="Pick a silo + library tier"
                  disabled={!messages?.length}
                  onClick={openCommit}
                  testid="bump-action-commit-silo"
                />
              </>
            )}
          </div>
          {/* Jun 2026 — Voice picker lives in its OWN section, dropped to the
              middle of the shelf and clearly separated from the action buttons
              above (per creator request). Shown to everyone when the creator
              allowed visitors to switch how the spoken answers sound. */}
          {voiceUserSelectable && (
            <div className="mt-4" data-testid="bump-voice-section">
              <div className="rounded-md border hairline px-3 py-2.5" data-testid="bump-voice-picker">
                <div className="flex items-center gap-2 text-[#1C1B1A] mb-1">
                  <Phosphor.SpeakerHigh size={16} weight="fill" />
                  <span className="text-sm font-medium">Voice</span>
                </div>
                <p className="text-[11px] text-[#6B6A68] mb-2">Pick how the spoken answers sound.</p>
                <select
                  data-testid="bump-voice-select"
                  value={selectedVoice || ""}
                  onChange={(e) => onSelectVoice?.(e.target.value)}
                  className="w-full text-sm rounded-md border hairline bg-white px-2 py-2 text-[#1C1B1A] focus:outline-none focus:ring-1"
                >
                  {voiceOptions.map((v) => (
                    <option key={v.id} value={v.id}>{v.label}</option>
                  ))}
                </select>
                {/* Jun 2026 — Randy wanted the speech-to-speech announcement
                    LOUD because it's the coolest feature. Animated gradient
                    callout: pulsing live dot, breathing mic, equalizer bars. */}
                <div
                  className="mt-3 relative overflow-hidden rounded-xl p-[1.5px] bg-gradient-to-r from-emerald-400 via-teal-400 to-violet-500 shadow-[0_0_18px_-2px_rgba(16,185,129,0.55)]"
                  data-testid="bump-voice-tip"
                >
                  <span className="absolute -top-1 -right-1 z-10 flex h-3.5 w-3.5">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-3.5 w-3.5 bg-emerald-500"></span>
                  </span>
                  <div className="relative rounded-[10px] bg-[#0c1310] px-3 py-3">
                    <div className="flex items-center gap-2 mb-1.5">
                      <Phosphor.Microphone size={18} weight="fill" className="text-emerald-300 animate-pulse" />
                      <span className="text-[10px] font-bold tracking-[0.18em] uppercase bg-gradient-to-r from-emerald-300 to-violet-300 bg-clip-text text-transparent">
                        New · Talk out loud
                      </span>
                      <span className="flex items-end gap-[2px] h-3.5 ml-auto" aria-hidden="true">
                        {[60, 100, 45, 85, 70].map((h, i) => (
                          <span
                            key={i}
                            className="w-[3px] bg-gradient-to-t from-emerald-500 to-teal-300 rounded-full animate-pulse"
                            style={{ height: `${h}%`, animationDelay: `${i * 110}ms`, animationDuration: "900ms" }}
                          />
                        ))}
                      </span>
                    </div>
                    <p className="text-[13px] font-bold text-white leading-snug">
                      Have a real voice conversation with this silo. 🔊
                    </p>
                    <p className="text-[11px] text-emerald-100/80 mt-1 leading-snug">
                      Tap the <span className="font-semibold text-white">waveform button</span> next to the
                      chat box and just start talking.
                    </p>
                  </div>
                </div>

              </div>
            </div>
          )}
          {/* Jun 2026 — Visitor billing: the "Unlock interactions" dot board
              lives UNDER the Voice feature (per Randy's spec). Tracks the
              visitor's interaction tokens + offers tip/subscribe when empty. */}
          <UnlockInteractions
            interactions={interactions}
            signedIn={!!getToken()}
            onFavorite={onInteractionFavorite}
            onAddHomeScreen={onInteractionAddHomeScreen}
            onTip={onInteractionTip}
            onSubscribe={onInteractionSubscribe}
          />
        </div>
      </Drawer>)}

      {/* === Upper bump — the bookmark shelf (id=bookmarks) === */}
      {bookmarksCfg && !tabsOff && (
      <BumpTab
        side={mode}
        open={openShelf}
        onOpen={() => setOpenShelf((v) => !v)}
        label={`Bookmarks${totalCount ? ` · ${totalCount}` : ""}`}
        icon={BookmarkSimple}
        testid={`${mode}-bump-shelf-tab`}
        cfg={bookmarksCfg}
        maxSlots={sideCfg?.max_slots}
      />)}
      {bookmarksCfg && (
      <Drawer open={openShelf} side={mode} large onClose={() => setOpenShelf(false)} testid={`${mode}-bump-shelf-drawer`} title={bookmarksCfg.name || "Bookmark shelf"} cfg={bookmarksCfg}>
        {!isAuthed && ghosts.length === 0 && (
          <EmptyShelfHelper onClickBookmark={onClickBookmark} />
        )}
        {isAuthed && bookmarks.length === 0 && !loadingBookmarks && (
          <EmptyShelfHelper onClickBookmark={onClickBookmark} authed />
        )}

        {sections.map((sec) => (
          <ShelfSection key={sec.name} section={sec}
            isAuthed={isAuthed}
            onRemove={removeBookmark}
            onRename={renameBookmark}
            onPushToStaging={openPushToStaging}
            onTap={onTapBookmark}
            siloSlug={silo.slug}
            onPlayAudio={playBmAudio}
            playingBmId={playingBmId}
          />
        ))}
        {!isAuthed && ghosts.length > 0 && (
          <div className="mt-4 p-3 rounded-md bg-[#FFF3DD] border border-[#E0C988] text-[11px] text-[#8A5A00]" data-testid="ghost-shelf-cta">
            <Lock size={12} weight="duotone" className="inline mb-0.5 mr-1" />
            Ghost bookmarks aren't clickable. <button onClick={() => setSignupNudge({ open: true, kind: "anon" })} className="underline font-medium">Sign up</button> to bring them to life.
          </div>
        )}
      </Drawer>)}

      {/* === Modals === */}
      <BookmarkModal
        open={bookmarkModal.open}
        ghost={bookmarkModal.ghost}
        defaultTitle={firstNWords(messages?.find?.((m) => m.role === "user")?.text || messages?.[0]?.text || "", 7)}
        existingSections={Array.from(new Set((isAuthed ? bookmarks : ghosts).map((b) => b.section_name).filter(Boolean)))}
        onSave={saveBookmark}
        onClose={() => setBookmarkModal({ open: false })}
      />
      <BookmarkOverwriteModal
        open={overwriteConfirm.open}
        existing={overwriteConfirm.existing}
        payload={overwriteConfirm.payload}
        onCancel={() => setOverwriteConfirm({ open: false, existing: null, payload: null, ghost: false })}
        onConfirm={async () => {
          const { existing, payload, ghost } = overwriteConfirm;
          setOverwriteConfirm({ open: false, existing: null, payload: null, ghost: false });
          // Delete the old bookmark first (ghost or DB), then re-call save
          // with the confirmed flag so we skip the duplicate guard.
          if (existing.ghost || !isAuthed) {
            const next = ghosts.filter((g) => g.id !== existing.id);
            try {
              localStorage.setItem(GHOST_KEY_PREFIX + silo.slug, JSON.stringify(next));
              setGhosts(next);
            } catch { /* best-effort */ }
          } else {
            try {
              await api.delete(`/bookmarks/${existing.id}`);
              setBookmarks((prev) => prev.filter((x) => x.id !== existing.id));
            } catch {
              toast.error("Could not replace the existing bookmark.");
              return;
            }
          }
          await saveBookmark({ ...payload, ghost, _confirmedOverwrite: true });
          toast.success("Bookmark replaced with the newer growth.");
        }}
      />
      <SignupNudgeModal
        open={signupNudge.open}
        kind={signupNudge.kind}
        ghostCount={ghosts.length}
        onContinueGhost={continueAsGhost}
        onClose={() => setSignupNudge({ open: false, kind: "anon" })}
      />
      <SiloPickerModal
        open={siloPicker.open}
        mode={siloPicker.mode}
        messages={messages || []}
        defaultTitle={firstNWords(messages?.find?.((m) => m.role === "user")?.text || messages?.[0]?.text || "", 7)}
        onClose={() => setSiloPicker({ open: false, mode: siloPicker.mode })}
        onSuccess={() => { /* toast already handled inside the modal */ }}
      />
      <LoadBookmarkConfirmModal
        open={loadConfirm.open}
        bookmark={loadConfirm.target}
        onSaveCurrentFirst={() => {
          // Drop the load action; user will save the current chat as a new
          // bookmark, then re-tap the original target. Keeps the flow
          // explicit rather than chaining two modals.
          setLoadConfirm({ open: false, target: null });
          onClickBookmark();
        }}
        onDiscardAndLoad={() => {
          const target = loadConfirm.target;
          setLoadConfirm({ open: false, target: null });
          if (target) replayBookmark(target);
        }}
        onClose={() => setLoadConfirm({ open: false, target: null })}
      />
      {/* Feb 14, 2026 PM session #5 — push-to-staging picker. */}
      <PushToStagingModal
        open={pushStagingState.open}
        bookmark={pushStagingState.bookmark}
        onClose={closePushToStaging}
      />
    </>
  );
}

// =====================================================================
// Tab + Drawer primitives
// =====================================================================
function BumpTab({ side, open, onOpen, label, icon: Icon, testid, cfg, maxSlots }) {
  // iter-35g: when a `cfg` is passed (from Bump Studio), use its dimensions,
  // shape, color, slot position, name, icon. When cfg is null (legacy
  // fallback) use the previous hard-coded defaults — preserves behavior on
  // any surface that hasn't been wired to bumpConfig yet.
  const useCfg = !!cfg;
  const top = useCfg ? slotToTopPct(cfg.slot, maxSlots) : "62%";
  const width = useCfg ? cfg.width : 52;
  const height = useCfg ? cfg.height : 132;
  const mobileWidth = useCfg ? cfg.width : 44;
  const mobileHeight = useCfg ? cfg.height : 96;
  const shapeClass = useCfg
    ? (side === "right" ? SHAPE_CLASS_RIGHT[cfg.shape] : SHAPE_CLASS[cfg.shape])
    : (side === "right" ? "rounded-l-md border-r-0 shadow-md" : "rounded-r-md border-l-0 shadow-md");
  const colors = useCfg ? (COLOR_SLOT_VAR[cfg.color_slot] || COLOR_SLOT_VAR.surface) : null;
  // "None" is an explicit icon-suppressed sentinel; falsy/blank also suppresses
  // (defensive in case the patch came in with an empty string from the API).
  const iconKey = useCfg ? (cfg.icon || "Sparkle") : null;
  const showIcon = useCfg ? (iconKey !== "None") : true;
  const PhosIcon = useCfg ? (Phosphor[iconKey] || Phosphor.Sparkle) : Icon;
  const displayLabel = useCfg ? (cfg.name ?? "") : label;
  const showLabel = !!displayLabel;
  const textSize = useCfg ? cfg.text_size : 10;
  const sideAnchor = side === "right" ? "right-0" : "left-0";

  return (
    <button onClick={onOpen} data-testid={testid} aria-expanded={open}
      className={`fixed ${sideAnchor} z-30 flex flex-col items-center justify-center gap-1.5 border tpl-border tpl-surface transition-colors hover:tpl-accent ${shapeClass}`}
      style={{
        top,
        color: useCfg ? colors.fg : "var(--tpl-text)",
        background: useCfg ? colors.bg : undefined,
        width: `${mobileWidth}px`, height: `${mobileHeight}px`,
        // Override on >=sm via inline media query is impossible; we use CSS
        // custom properties so the breakpoint-aware values apply via class.
        // Easiest: inject a style tag would be overkill — instead pass
        // explicit width/height that match desktop OR mobile based on what
        // the cfg encodes (admin sets per-viewport dims separately).
        ...(useCfg ? {} : {}),
      }}
    >
      {showIcon && <PhosIcon size={Math.max(12, textSize + 4)} weight="duotone" />}
      {showLabel && (
        <span style={{ writingMode: "vertical-rl", transform: "rotate(180deg)", fontSize: `${textSize}px` }}
          className="uppercase tracking-[0.18em] font-medium whitespace-nowrap overflow-hidden">
          {displayLabel}
        </span>
      )}
    </button>
  );
}

function Drawer({ open, side, large, onClose, testid, title, cfg, children }) {
  // iter-35g: drawer width respects per-bump cfg.drawer_width when provided.
  // Sprint 3: also honor drawer_top_pct / drawer_bottom_pct (vertical inset)
  // and drawer_style (5 visual variants). Falls back to legacy w-72/w-96 +
  // top-0/bottom-0 + sharp-edge classic if cfg is missing/null.
  const widthStyle = cfg?.drawer_width ? { width: `${cfg.drawer_width}px` } : undefined;
  const widthClass = cfg?.drawer_width ? "" : (large ? "w-80 sm:w-96" : "w-72 sm:w-96");
  const topPct = cfg?.drawer_top_pct ?? 0;
  const bottomPct = cfg?.drawer_bottom_pct ?? 0;
  const insetStyle = (topPct || bottomPct)
    ? { ...(widthStyle || {}), top: `${topPct}vh`, bottom: `${bottomPct}vh` }
    : widthStyle;
  const insetClass = (topPct || bottomPct) ? "" : "top-0 bottom-0";
  const drawerStyle = cfg?.drawer_style || "slide-rect";
  // Style variant → extra Tailwind classes layered on top of the base drawer.
  const STYLE_CLASSES = {
    "slide-rect": "tpl-surface border-l border-r tpl-border shadow-2xl",
    "slide-rounded": "tpl-surface border tpl-border shadow-2xl rounded-2xl",
    "slide-card": (side === "right" ? "tpl-surface border tpl-border shadow-2xl rounded-l-2xl mr-1 my-2" : "tpl-surface border tpl-border shadow-2xl rounded-r-2xl ml-1 my-2"),
    "slide-frosted": "border tpl-border shadow-2xl backdrop-blur-md bg-white/70",
    "slide-edgeless": "tpl-surface shadow-xl",
    "slide-tabbed": "tpl-surface border-l-4 border-r tpl-border shadow-2xl [border-left-color:var(--tpl-accent)]",
  };
  const styleClasses = STYLE_CLASSES[drawerStyle] || STYLE_CLASSES["slide-rect"];
  return (
    <AnimatePresence>
      {open && (
        <>
          {/* Click-outside-to-close backdrop. Sits behind the drawer (lower z)
              but above page content. Tap anywhere off the drawer → close. */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.18 }}
            onClick={onClose}
            className="fixed inset-0 z-[79] bg-transparent"
            data-testid={`${testid}-backdrop`}
          />
          <motion.div
            initial={{ x: side === "right" ? 320 : -320, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: side === "right" ? 320 : -320, opacity: 0 }}
            transition={{ type: "tween", duration: 0.22 }}
            onClick={onClose}
            className={`fixed ${insetClass} ${side === "right" ? "right-0" : "left-0"} ${widthClass} z-[80] ${styleClasses} flex flex-col cursor-pointer`}
            style={insetStyle}
            data-testid={testid}
          >
            <div className="flex items-center px-4 py-3 border-b tpl-border">
              <div className="text-sm font-semibold uppercase tracking-[0.18em]" style={{ color: "var(--tpl-text)" }}>{title}</div>
              <span className="ml-auto text-[10px] uppercase tracking-[0.2em] tpl-muted opacity-60">Tap anywhere to close</span>
            </div>
            <div
              className="flex-1 overflow-y-auto p-4 cursor-default"
              onClick={(e) => {
                if (e.target.closest("button, a, input, textarea, label, select")) {
                  e.stopPropagation();
                }
              }}
            >
              {children}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}

function ActionButton({ icon: Icon, label, sub, disabled, onClick, testid }) {
  return (
    <button onClick={onClick} disabled={disabled} data-testid={testid}
      className="w-full text-left p-3 rounded-md border tpl-border tpl-surface hover:tpl-accent transition-colors flex items-start gap-3 disabled:opacity-40 disabled:cursor-not-allowed">
      <Icon size={18} weight="duotone" className="shrink-0 mt-0.5" />
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium" style={{ color: "var(--tpl-text)" }}>{label}</div>
        <div className="text-[11px] tpl-muted mt-0.5">{sub}</div>
      </div>
    </button>
  );
}

function EmptyShelfHelper({ onClickBookmark, authed }) {
  return (
    <div className="text-center py-6 px-4" data-testid="empty-shelf-helper">
      <BookmarkSimple size={32} weight="duotone" className="mx-auto opacity-50" />
      <p className="text-sm mt-2" style={{ color: "var(--tpl-text)" }}>No bookmarks yet.</p>
      <p className="text-[11px] tpl-muted mt-1">
        {authed ? "Save the current conversation to a section so you can find it later." : "Bookmark a conversation to start your shelf."}
      </p>
      <button onClick={onClickBookmark} data-testid="empty-shelf-cta"
        className="mt-3 text-xs px-3 py-1.5 rounded-md tpl-accent-bg">
        Bookmark current view
      </button>
    </div>
  );
}

function ShelfSection({ section, isAuthed, onRemove, onRename, onPushToStaging, onTap, siloSlug, onPlayAudio, playingBmId }) {
  const [open, setOpen] = useState(true);
  return (
    <div className="mb-3" data-testid={`shelf-section-${section.name}`}>
      <button onClick={() => setOpen((v) => !v)} className="w-full flex items-center gap-1.5 px-2 py-1.5 rounded text-xs uppercase tracking-[0.18em] tpl-muted hover:tpl-accent">
        {open ? <CaretDown size={10} /> : <CaretRight size={10} />}
        <span className="font-semibold">{section.name}</span>
        <span className="ml-auto tabular-nums">{section.items.length}</span>
      </button>
      {open && (
        <div className="mt-1 space-y-1">
          {section.items.map((b) => (
            <ShelfFileRow key={b.id} bookmark={b} isAuthed={isAuthed} onRemove={onRemove} onRename={onRename} onPushToStaging={onPushToStaging} onTap={onTap} siloSlug={siloSlug} onPlayAudio={onPlayAudio} playingBmId={playingBmId} />
          ))}
        </div>
      )}
    </div>
  );
}

// Jun 2026 (Randy) — bookmark audio icon: GREEN + playable only when the saved
// audio is COMPLETE and still alive; partial/none → greyed (tap loads the convo).
const bmAudioComplete = (b) => {
  if (!b?.has_audio || !b?.audio_complete || !b?.audio_expires_at) return false;
  try { return new Date(b.audio_expires_at).getTime() > Date.now(); } catch { return false; }
};

function ShelfFileRow({ bookmark, isAuthed, onRemove, onRename, onPushToStaging, onTap, siloSlug, onPlayAudio, playingBmId }) {
  // Feb 14, 2026 PM session #5 — Removed the inline rename editor +
  // pencil button per user request. Renaming a bookmark after creation
  // had no value. Pencil replaced with an Export button that opens the
  // "push to workbench staging" picker (lives in the parent).
  const ghost = !!bookmark.ghost;
  const complete = !ghost && bmAudioComplete(bookmark);
  const playing = playingBmId === bookmark.id;

  return (
    <div className={`flex items-center gap-1 p-2 rounded-md border tpl-border ${ghost ? "bg-[#FAF6EE]" : "tpl-surface"}`} data-testid={`shelf-file-${bookmark.id}`}>
      <BookmarkSimple size={12} weight={ghost ? "regular" : "fill"} className="shrink-0" style={{ color: ghost ? "#A89A7A" : "var(--tpl-accent-color, #4A5D4E)" }} />
      <button onClick={() => onTap && onTap(bookmark)}
        className={`flex-1 min-w-0 text-left text-xs truncate ${ghost ? "tpl-muted cursor-not-allowed" : "hover:underline"}`}
        title={ghost ? "Sign up to access this bookmark" : "Tap to load this conversation"}
        data-testid={`shelf-file-title-${bookmark.id}`}
      >
        {bookmark.file_title}
      </button>
      {!ghost && (
        <button
          onClick={() => (complete ? onPlayAudio && onPlayAudio(bookmark) : onTap && onTap(bookmark))}
          className={`p-1 ${complete ? "text-[#27AE60] hover:opacity-80" : "opacity-40 hover:opacity-70"}`}
          data-testid={`shelf-file-audio-${bookmark.id}`}
          title={complete ? (playing ? "Stop" : "Play the whole conversation (free — already paid)") : "No complete audio yet — open to finish it"}
          aria-label="Play saved audio"
        >
          {complete
            ? (playing ? <Phosphor.Pause size={12} weight="fill" /> : <Phosphor.SpeakerHigh size={12} weight="fill" />)
            : <Phosphor.SpeakerSlash size={12} weight="duotone" />}
        </button>
      )}
      {!ghost && (
        <button
          onClick={() => onPushToStaging && onPushToStaging(bookmark)}
          className="p-1 opacity-60 hover:opacity-100 hover:text-[#4A5D4E]"
          data-testid={`shelf-file-push-staging-${bookmark.id}`}
          title="Push to a silo's Workbench staging area"
          aria-label="Push to Workbench staging"
        >
          <Export size={11} weight="bold" />
        </button>
      )}
      {ghost && <Lock size={11} className="opacity-50" />}
      <button onClick={() => onRemove(bookmark)} className="p-1 opacity-50 hover:opacity-100 hover:text-[#C16E5A]"
        data-testid={`shelf-file-remove-${bookmark.id}`}>
        <Trash size={11} />
      </button>
    </div>
  );
}

// =====================================================================
// BookmarkModal — section picker + file title input
// =====================================================================
function BookmarkModal({ open, ghost, defaultTitle, existingSections, onSave, onClose }) {
  const [section, setSection] = useState("");
  const [newSectionMode, setNewSectionMode] = useState(false);
  const [title, setTitle] = useState("");

  useEffect(() => {
    if (open) {
      setTitle(defaultTitle || "Untitled");
      setSection(existingSections[0] || "");
      setNewSectionMode(existingSections.length === 0);
    }
  }, [open, defaultTitle, existingSections]);

  if (!open) return null;

  const submit = () => {
    const sec = (newSectionMode ? section : section || "Untitled section").trim();
    if (!sec) return toast.error("Section name is required.");
    if (!title.trim()) return toast.error("File title is required.");
    onSave({ section_name: sec, file_title: title.trim(), ghost });
  };

  return (
    // z-[90] for the same reason as SignupNudgeModal below: these open FROM
    // the shelf drawer, whose transparent backdrop sits at z-[79].
    <div className="fixed inset-0 z-[90] bg-black/40 flex items-center justify-center p-4" data-testid="bookmark-modal" onClick={onClose}>
      <div className="modal-safe bg-white rounded-xl border hairline shadow-2xl w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
        <h3 className="font-serif-display text-2xl flex items-center gap-2">
          <BookmarkSimple size={22} weight="duotone" className="text-[#4A5D4E]" />
          {ghost ? "Save as ghost bookmark" : "Bookmark this conversation"}
        </h3>
        {ghost && <p className="text-xs text-[#8A5A00] mt-1">This will be saved locally and won't be clickable until you sign up.</p>}

        <label className="block text-xs uppercase tracking-[0.18em] text-[#6B6A68] mt-4 mb-1.5">Section</label>
        {newSectionMode ? (
          <div className="flex gap-2">
            <input value={section} onChange={(e) => setSection(e.target.value)} autoFocus
              data-testid="bookmark-section-new"
              placeholder="e.g. Book Chapter 1"
              className="flex-1 bg-white border hairline rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#4A5D4E]" />
            {existingSections.length > 0 && (
              <button onClick={() => setNewSectionMode(false)} className="text-xs text-[#6B6A68] underline">Pick existing</button>
            )}
          </div>
        ) : (
          <div className="flex gap-2">
            <select value={section} onChange={(e) => setSection(e.target.value)}
              data-testid="bookmark-section-select"
              className="flex-1 bg-white border hairline rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#4A5D4E]">
              {existingSections.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
            <button onClick={() => { setSection(""); setNewSectionMode(true); }} data-testid="bookmark-section-newbtn"
              className="text-xs px-3 py-2 rounded-md border hairline hover:border-[#4A5D4E] hover:text-[#4A5D4E] flex items-center gap-1">
              <Plus size={12} /> New section
            </button>
          </div>
        )}

        <label className="block text-xs uppercase tracking-[0.18em] text-[#6B6A68] mt-4 mb-1.5">File title</label>
        <input value={title} onChange={(e) => setTitle(e.target.value)}
          data-testid="bookmark-title"
          placeholder="Defaults to first 7 words"
          className="w-full bg-white border hairline rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#4A5D4E]" />

        <div className="mt-5 flex justify-end gap-2">
          <button onClick={onClose} data-testid="bookmark-cancel" className="text-sm px-4 py-2 rounded-md text-[#6B6A68] hover:text-[#1C1B1A]">Cancel</button>
          <button onClick={submit} data-testid="bookmark-save"
            className="text-sm px-4 py-2 rounded-md bg-[#1C1B1A] text-[#FDFCF8] hover:bg-[#3A4A3E] flex items-center gap-1.5">
            <FloppyDisk size={14} /> {ghost ? "Save (ghost)" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}

// =====================================================================
// SignupNudgeModal — anonymous-funnel + subscriber-cap upgrade prompt
// =====================================================================
function SignupNudgeModal({ open, kind, ghostCount, onContinueGhost, onClose }) {
  const navigate = useNavigate();
  if (!open) return null;
  const isAnon = kind === "anon";
  return (
    // Jun 2026 — z-[90], above the shelf drawer (z-[80]) and its transparent
    // backdrop (z-[79]). At z-50 the drawer's invisible backdrop painted over
    // this modal and swallowed every click on "Continue as ghost", so the
    // anonymous bookmark path was unreachable from the Bookmarks bump.
    <div className="fixed inset-0 z-[90] bg-black/50 flex items-center justify-center p-4" data-testid="signup-nudge-modal" onClick={onClose}>
      <div className="modal-safe bg-white rounded-xl border hairline shadow-2xl w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
        <h3 className="font-serif-display text-2xl flex items-center gap-2">
          <Sparkle size={22} weight="duotone" className="text-[#4A5D4E]" />
          {isAnon ? "Sign up to unlock bookmarks" : "Upgrade to Creator for unlimited bookmarks"}
        </h3>
        <ul className="mt-4 space-y-2 text-sm text-[#1C1B1A]">
          <li>· <strong>Subscribers</strong> get 1 bookmark per silo</li>
          <li>· <strong>Creators</strong> get unlimited bookmarks across every silo</li>
          {isAnon && <li>· <strong>Anonymous</strong> visitors can save up to {3} ghost bookmarks (visible but locked)</li>}
        </ul>
        {isAnon && (
          <p className="text-xs text-[#6B6A68] mt-3">
            You currently have <strong>{ghostCount}</strong> ghost bookmark{ghostCount === 1 ? "" : "s"}. Sign up and they'll all become real, clickable bookmarks.
          </p>
        )}
        <div className="mt-6 flex flex-wrap justify-end gap-2">
          {isAnon && ghostCount < 3 && (
            <button onClick={onContinueGhost} data-testid="signup-nudge-ghost"
              className="text-sm px-4 py-2 rounded-md border hairline text-[#6B6A68] hover:border-[#4A5D4E] hover:text-[#4A5D4E]">
              Continue as ghost
            </button>
          )}
          <button onClick={onClose} data-testid="signup-nudge-dismiss" className="text-sm px-4 py-2 rounded-md text-[#6B6A68] hover:text-[#1C1B1A]">Maybe later</button>
          <button
            data-testid="signup-nudge-signup"
            onClick={() => { onClose(); navigate("/signup"); }}
            className="text-sm px-5 py-2 rounded-md bg-[#1C1B1A] text-[#FDFCF8] hover:bg-[#3A4A3E] flex items-center gap-1.5">
            <ArrowSquareOut size={14} /> {isAnon ? "Create free account" : "Become a Creator"}
          </button>
        </div>
      </div>
    </div>
  );
}


// =====================================================================
// LoadBookmarkConfirmModal — Sprint Feb 7, 2026
// Shown when tapping a bookmark while there's an unsaved live chat. Lets
// the user save the current chat first, discard it and load the target,
// or cancel.
// =====================================================================
function LoadBookmarkConfirmModal({ open, bookmark, onSaveCurrentFirst, onDiscardAndLoad, onClose }) {
  if (!open || !bookmark) return null;
  return (
    <div className="fixed inset-0 z-[90] flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm" onClick={onClose} data-testid="load-bookmark-confirm-modal">
      <div className="modal-safe bg-white rounded-xl max-w-md w-full p-6 shadow-xl" onClick={(e) => e.stopPropagation()}>
        <h3 className="font-serif-display text-xl text-[#1C1B1A]">Load this conversation?</h3>
        <p className="text-sm text-[#6B6A68] mt-2">
          You've got unsaved messages in the current view. Loading{" "}
          <strong className="text-[#1C1B1A]">"{bookmark.file_title}"</strong>{" "}
          will replace what's here.
        </p>
        <div className="mt-5 flex flex-col gap-2">
          <button
            onClick={onSaveCurrentFirst}
            data-testid="load-bookmark-save-current"
            className="w-full text-sm px-4 py-2.5 rounded-md bg-[#1C1B1A] text-[#FDFCF8] hover:bg-[#3A4A3E] text-left">
            <strong>Save current as bookmark first</strong>
            <span className="block text-[11px] text-[#FDFCF8]/70 mt-0.5">
              Then come back and tap this bookmark again.
            </span>
          </button>
          <button
            onClick={onDiscardAndLoad}
            data-testid="load-bookmark-discard"
            className="w-full text-sm px-4 py-2.5 rounded-md border hairline text-[#1C1B1A] hover:border-[#C16E5A] hover:text-[#C16E5A] text-left">
            <strong>Discard and load</strong>
            <span className="block text-[11px] text-[#6B6A68] mt-0.5">
              Throw away the current chat and load the bookmark.
            </span>
          </button>
          <button
            onClick={onClose}
            data-testid="load-bookmark-cancel"
            className="w-full text-sm px-4 py-2 rounded-md text-[#6B6A68] hover:text-[#1C1B1A]">
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

// Sprint Feb 8, 2026 — duplicate-name confirmation. Triggered when a
// bookmark save collides with an existing (section_name, file_title)
// pair. Confirming overwrites the older entry with the newer growth.
function BookmarkOverwriteModal({ open, existing, payload, onCancel, onConfirm }) {
  if (!open || !existing || !payload) return null;
  return (
    <div
      className="fixed inset-0 z-[90] bg-black/40 flex items-center justify-center p-4"
      data-testid="bookmark-overwrite-modal"
      onClick={onCancel}
    >
      <div className="modal-safe bg-white rounded-xl border hairline shadow-2xl w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
        <h3 className="font-serif-display text-2xl text-[#1C1B1A]">A bookmark with that name already exists</h3>
        <p className="text-sm text-[#6B6A68] mt-2 leading-relaxed">
          You already have a bookmark named <strong className="text-[#1C1B1A]">"{payload.file_title}"</strong> in section <strong className="text-[#1C1B1A]">"{payload.section_name}"</strong>.
        </p>
        <p className="text-sm text-[#6B6A68] mt-2 leading-relaxed">
          Overwrite it with this newer conversation?
        </p>
        <div className="mt-5 flex justify-end gap-2">
          <button
            onClick={onCancel}
            data-testid="bookmark-overwrite-cancel"
            className="text-sm px-4 py-2 rounded-md text-[#6B6A68] hover:text-[#1C1B1A]"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            data-testid="bookmark-overwrite-confirm"
            className="text-sm px-4 py-2 rounded-md bg-[#C16E5A] text-[#FDFCF8] hover:bg-[#A85742] flex items-center gap-1.5"
          >
            Yes, overwrite
          </button>
        </div>
      </div>
    </div>
  );
}



// =====================================================================
// PushToStagingModal — pick a silo, push the bookmark's conversation
// into that silo's Workbench staging area. Replaces the legacy rename
// pencil per user request Feb 14, 2026 PM session #5.
// =====================================================================
function PushToStagingModal({ open, bookmark, onClose }) {
  const [silos, setSilos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState(null);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    (async () => {
      try {
        const r = await api.get("/silos");
        setSilos(r.data || []);
      } catch (e) {
        toast.error(e.response?.data?.detail || "Could not load your silos.");
        setSilos([]);
      } finally {
        setLoading(false);
      }
    })();
  }, [open]);

  if (!open) return null;

  // Render the bookmark conversation as markdown for the staging field.
  // We include role labels so the workbench user can clearly see what
  // came from the visitor vs. the AI when they review it.
  const renderConversation = () => {
    if (!bookmark) return "";
    const messages = bookmark.messages || [];
    const lines = [
      `# ${bookmark.file_title || "Bookmarked conversation"}`,
      `_Pushed from "${bookmark.section_name || "Untitled"}" bookmarks · ${new Date().toISOString().slice(0, 10)}_`,
      "",
    ];
    for (const m of messages) {
      const role = m.role === "user" ? "**Visitor**" : "**AI**";
      lines.push(`${role}: ${m.text || ""}`);
      lines.push("");
    }
    return lines.join("\n");
  };

  const pushTo = async (siloId, siloName) => {
    if (!bookmark) return;
    setBusyId(siloId);
    try {
      // Read the existing staging text so we append rather than nuke it.
      let existing = "";
      try {
        const cur = await api.get(`/silos/${siloId}/workbench-state`);
        existing = cur.data?.staging || "";
      } catch { /* if it errors, treat as empty */ }
      const dump = renderConversation();
      const combined = existing.trim()
        ? `${existing.trim()}\n\n---\n\n${dump}`
        : dump;
      await api.patch(`/silos/${siloId}/workbench-state`, { staging: combined });
      toast.success(`Pushed to ${siloName} · Workbench staging.`);
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Push failed.");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] bg-black/40 flex items-center justify-center p-4" data-testid="push-to-staging-modal" onClick={onClose}>
      <div className="modal-safe bg-white rounded-xl border hairline shadow-2xl w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-2">
          <h3 className="font-serif-display text-2xl flex items-center gap-2">
            <Export size={20} weight="duotone" className="text-[#4A5D4E]" />
            Push to Workbench staging
          </h3>
          <button onClick={onClose} className="text-[#6B6A68] hover:text-[#1C1B1A]" aria-label="Close">
            <X size={16} />
          </button>
        </div>
        <p className="text-xs text-[#6B6A68] mb-4">
          Sends this conversation into a silo's Workbench staging area, ready for you to refine into corpus. Existing staging text is preserved — the bookmark gets appended.
        </p>

        {bookmark && (
          <div className="mb-4 p-3 rounded-md bg-[#FAF7F2] border border-[#E8E5DF]">
            <div className="text-[10px] uppercase tracking-[0.18em] text-[#6B6A68]">Pushing</div>
            <div className="text-sm font-medium text-[#1C1B1A] truncate">{bookmark.file_title}</div>
            <div className="text-[10px] text-[#6B6A68] mt-0.5">{(bookmark.messages || []).length} message{(bookmark.messages || []).length === 1 ? "" : "s"} · from "{bookmark.section_name || "Untitled"}"</div>
          </div>
        )}

        <label className="block text-xs uppercase tracking-[0.18em] text-[#6B6A68] mb-2">Pick a silo</label>
        {loading ? (
          <div className="text-xs text-[#6B6A68] py-4 text-center">Loading your silos…</div>
        ) : silos.length === 0 ? (
          <div className="text-xs text-[#6B6A68] py-4 text-center">
            You don't own any silos yet. <Link to="/dashboard" className="text-[#4A5D4E] underline" onClick={onClose}>Create one first</Link>.
          </div>
        ) : (
          <div className="max-h-80 overflow-y-auto space-y-1.5" data-testid="push-to-staging-silos">
            {silos.map((s) => (
              <button
                key={s.id}
                type="button"
                onClick={() => pushTo(s.id, s.name)}
                disabled={busyId !== null}
                className="w-full text-left p-2.5 rounded-md border border-[#D8D6D2] bg-white hover:border-[#4A5D4E] hover:bg-[#FAFAFA] disabled:opacity-50 disabled:cursor-not-allowed transition flex items-center justify-between gap-2"
                data-testid={`push-to-staging-silo-${s.slug}`}
              >
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium text-[#1C1B1A] truncate">{s.name}</div>
                  <div className="text-[10px] text-[#6B6A68] truncate">/{s.slug}</div>
                </div>
                {busyId === s.id ? (
                  <span className="text-[10px] uppercase tracking-[0.18em] text-[#4A5D4E]">Pushing…</span>
                ) : (
                  <Export size={14} weight="bold" className="text-[#4A5D4E] shrink-0" />
                )}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
